import { useEffect, useState } from 'react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, PermissionDenied, TableSkeleton } from '@/components/States'
import { ApiError } from '@/lib/api'
import { fetchModules, toggleModule, PLAT_STR as S, type PlatformModule } from './platformApi'

/**
 * A0 — Модули платформы (kill-switch). `GET /admin/modules` → 8 feature-modul;
 * har birini yoqish/o'chirish `POST /admin/modules/:key/toggle` (SUPERADMIN —
 * server majburlaydi; 403 → <PermissionDenied>). Read soft: oddiy admin ko'radi,
 * toggle faqat superadminда ishlaydi.
 */
export function ModuleTogglesScreen() {
  const { toast } = useToast()
  const [mods, setMods] = useState<PlatformModule[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  useEffect(() => {
    const ac = new AbortController()
    fetchModules(ac.signal)
      .then((m) => setMods(m))
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [])

  const flip = async (m: PlatformModule) => {
    const next = !m.enabled
    setBusy(m.key)
    try {
      await toggleModule(m.key, next)
      setMods((prev) => prev?.map((x) => (x.key === m.key ? { ...x, enabled: next } : x)) ?? prev)
      toast(S.toggled, { tone: 'success' })
    } catch (e) {
      if (e instanceof ApiError && e.isForbidden) toast(S.toggleFailed, { tone: 'error' })
      else toast(S.toggleFailed, { tone: 'error' })
    } finally {
      setBusy(null)
    }
  }

  if (err?.isForbidden) return <PermissionDenied permission="modules:toggle" />

  return (
    <>
      <PageHeader title={S.modules} screenId="A0" description={S.modulesDesc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={() => location.reload()} />
        ) : !mods ? (
          <TableSkeleton rows={8} columns={2} />
        ) : (
          <div className="mx-auto grid max-w-3xl gap-2.5 sm:grid-cols-2">
            {mods.map((m) => (
              <div
                key={m.key}
                className="flex items-center justify-between gap-3 rounded-lg border border-border-default bg-surface-2 p-3.5"
              >
                <div className="min-w-0">
                  <p className="truncate text-base text-primary">{m.title}</p>
                  <p className="font-mono text-xs text-tertiary">{m.key}</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={m.enabled}
                  disabled={busy !== null}
                  onClick={() => flip(m)}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                    m.enabled ? 'bg-accent' : 'bg-surface-3'
                  }`}
                  title={m.enabled ? S.on : S.off}
                >
                  <span
                    className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                      m.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
